import React, { useMemo } from 'react'

import Link from 'next/link'
import {
  HiBars3,
  HiMiniChevronLeft,
  HiMiniChevronRight,
  HiOutlineRocketLaunch,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import Button from '@/components/common/Button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import Intercom from '@/components/layouts/common/Navbar/Intercom'
import { NotificationFeed } from '@/components/layouts/common/Navbar/NotificationFeed'
import ProfileDropdown from '@/components/layouts/common/Navbar/ProfileDropdown'

import { createLoginUrlWithRedirect } from '@/helpers/login'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useMounted } from '@/hooks/common/useMounted'
import { useSafePush } from '@/hooks/navigation/useSafePush'

import { useSidebarStore } from '@/stores/SidebarStore'

import type { Breadcrumb } from '@/types/navigation/Navbar'

interface Navbar {
  breadcrumbs: Array<Breadcrumb>
  isLoading?: boolean
  hasNavTabs?: boolean
  backTo?: string
  backFn?: () => void
  // Optional left-side content rendered IN PLACE of the breadcrumbs (e.g. a
  // section tab strip), so a section can use the navbar's single row instead of
  // a separate bar. When set, breadcrumbs/isLoading are ignored.
  leadingContent?: React.ReactNode
}

const Navbar: React.FC<Navbar> = ({
  breadcrumbs,
  isLoading = false,
  hasNavTabs = false,
  backTo,
  backFn,
  leadingContent,
}) => {
  const { user } = useAuthInfo()
  const { safePush } = useSafePush()
  const { data: billingOrganization } = useBillingOrganization()

  const isMounted = useMounted()

  const redirect = typeof window !== 'undefined' ? window.location.href : ''

  const { toggleSidebar } = useSidebarStore()

  const last: Breadcrumb = breadcrumbs[breadcrumbs.length - 1]

  const handleBreadcrumbClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string | undefined,
  ) => {
    // breadcrumbs as links seem to fail in proposal writer;
    // TODO: figure out why (08/06/2024)
    e.preventDefault()
    if (href !== undefined) {
      safePush(href)
    }
  }

  const sidebarButton = useMemo(() => {
    return (
      <button
        className={twMerge(
          'flex items-center gap-x-1.5 cursor-pointer rounded-md p-[6px] hover:bg-primary-100 dark:hover:bg-primary-800 transition duration-100 border',
          'border-primary-300 dark:border-primary-700',
        )}
        onClick={toggleSidebar}
      >
        <HiBars3 className="h-5 w-5 text-primary-500 dark:text-primary-400" />
      </button>
    )
  }, [toggleSidebar])

  const backButton =
    backFn !== undefined && backTo !== undefined ? (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild={true}>
          <button
            className={twMerge(
              'flex items-center gap-x-1.5 cursor-pointer rounded-[5px] p-1 hover:bg-primary-100 dark:hover:bg-primary-800 transition duration-100 border',
              'border-primary-300 dark:border-primary-700',
            )}
            onClick={backFn}
          >
            <HiMiniChevronLeft className="h-4 w-4 text-primary-700 dark:text-primary-300" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <span>{backTo}</span>
        </TooltipContent>
      </Tooltip>
    ) : null

  return (
    <nav className="relative min-h-[50px]">
      <div className="relative flex flex-wrap justify-between items-center w-full min-h-[50px] mx-auto px-[14px]">
        <div className="flex items-center gap-x-4 w-full w-4/5 lg:w-5/6 max-w-[calc(100%-124px)] md:max-w-[calc(100%-400px)]">
          {backButton !== null && (
            <div className="hidden md:flex items-center">{backButton}</div>
          )}
          <div className="flex items-center gap-x-4 md:hidden">
            {sidebarButton}
          </div>
          {leadingContent ??
            (isLoading ? (
              <div className="h-2 w-24 md:w-48 rounded-md bg-primary-300 dark:bg-primary-600 animate-pulse" />
            ) : (
              breadcrumbs.length > 0 && (
                <div className="w-full overflow-x-auto no-scrollbar">
                  <ol className="hidden md:flex items-center gap-x-2 w-full">
                    {breadcrumbs.map((crumb, idx) => {
                      const isLast = idx === breadcrumbs.length - 1

                      return (
                        <li
                          key={idx}
                          className={twMerge(
                            'flex items-center gap-x-2 text-primary-900 dark:text-primary-200',
                            isLast
                              ? 'min-w-0 flex-1'
                              : 'shrink-0 max-w-[320px]',
                          )}
                        >
                          {crumb.node ? (
                            crumb.node
                          ) : crumb.href ? (
                            <a
                              href={crumb.href}
                              target={crumb.target}
                              onClick={(e) =>
                                handleBreadcrumbClick(e, crumb.href)
                              }
                              className="truncate overflow-ellipsis self-center text-sm font-medium whitespace-nowrap hover:opacity-70 transition-opacity duration-100"
                            >
                              <span>{crumb.name}</span>
                            </a>
                          ) : (
                            <div className="min-w-0">
                              <div className="truncate overflow-ellipsis self-center text-sm font-medium whitespace-nowrap">
                                {crumb.name}
                              </div>
                            </div>
                          )}
                          {!isLast && (
                            <div className="shrink-0">
                              <HiMiniChevronRight className="h-4 w-4 stroke-[0.7px] text-primary-400 dark:text-primary-500" />
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ol>
                  <div className="md:hidden">
                    {last.node ? (
                      last.node
                    ) : last.href ? (
                      <a
                        href={last.href}
                        target={last.target}
                        onClick={(e) => handleBreadcrumbClick(e, last.href)}
                        className="self-center text-sm font-medium whitespace-nowrap hover:opacity-70 transition-opacity duration-100"
                      >
                        <span className="w-36 block truncate">{last.name}</span>
                      </a>
                    ) : (
                      <div className="self-center text-sm font-medium whitespace-nowrap">
                        <span className="w-36 block truncate">{last.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
        </div>
        <div className="flex items-center justify-end gap-x-2 min-w-[124px] md:min-w-[356px]">
          {/* takes up 120px of space */}
          {user !== undefined && user !== null && (
            <div className="hidden md:block pr-1">
              <NotificationFeed />
            </div>
          )}
          {/* takes up 300px space */}
          {/* TODO: radix tooltip seems to not work nicely with SSR, need to investigate */}
          {isMounted && (
            <>
              {billingOrganization !== undefined &&
                billingOrganization !== null &&
                billingOrganization.tier === 'free' && (
                  <div className="hidden md:block pr-1">
                    <Link href="/upgrade">
                      <Button as="a" variant="primary-compact">
                        <HiOutlineRocketLaunch className="h-4 w-4 text-white" />
                        <span className="text-sm font-semibold">Upgrade</span>
                      </Button>
                    </Link>
                  </div>
                )}
            </>
          )}
          <div className="hidden md:block whitespace-nowrap">
            <Intercom />
          </div>
          {user?.pictureUrl && (
            // only show profile dropdown on mobile since the desktop version is in the sidebar
            <div className="md:hidden">
              <ProfileDropdown />
            </div>
          )}
          {user === null && (
            <div className="hidden md:block">
              <Link href={createLoginUrlWithRedirect(redirect)}>
                <Button variant="primary-compact">
                  <span className="text-sm font-semibold">Sign In</span>
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

export default Navbar
